import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Transaction {
  id: string
  order_id: string
  customer_email: string
  provider: string
  payment_method: string
  amount: number
  currency: string
  fee: number
  net_amount: number
  status: string
  created_at: string
}

interface Provider {
  id: string
  name: string
  payment_methods: string[]
  status: string
  is_primary: boolean
  transaction_fee_pct: number
  volume_today: number
  success_rate: number
}

interface Dispute {
  id: string
  transaction_id: string
  reason: string
  status: string
  amount: number
  currency: string
  evidence_due_date: string
}

export default function PaymentGatewayPage() {
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [providers, setProviders] = useState<Provider[]>([])
  const [disputes, setDisputes] = useState<Dispute[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [transRes, providersRes, disputesRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/payments/transactions'),
      fetch(API_BASE + '/payments/providers'),
      fetch(API_BASE + '/payments/disputes'),
      fetch(API_BASE + '/payments/statistics')
    ])
    if (transRes.ok) setTransactions((await transRes.json()).transactions || [])
    if (providersRes.ok) setProviders((await providersRes.json()).providers || [])
    if (disputesRes.ok) setDisputes((await disputesRes.json()).disputes || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'failed': return 'bg-red-100 text-red-800'
      case 'refunded': return 'bg-purple-100 text-purple-800'
      case 'open': return 'bg-yellow-100 text-yellow-800'
      case 'under_review': return 'bg-blue-100 text-blue-800'
      case 'won': return 'bg-green-100 text-green-800'
      case 'lost': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Payment Gateway</h1>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Transactions Today</div>
              <div className="text-2xl font-bold">{statistics.transactions_today}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Volume Today</div>
              <div className="text-2xl font-bold text-green-600">{formatCurrency(statistics.volume_today)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Fees Today</div>
              <div className="text-2xl font-bold text-gray-600">{formatCurrency(statistics.fees_today)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Success Rate</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.success_rate}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Open Disputes</div>
              <div className="text-2xl font-bold text-orange-600">{statistics.open_disputes}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-4 gap-4 mb-8">
          {providers.map(provider => (
            <div key={provider.id} className={`bg-white rounded-lg shadow p-4 ${provider.is_primary ? 'ring-2 ring-blue-500' : ''}`}>
              <div className="flex justify-between items-start mb-2">
                <div>
                  <div className="font-semibold">{provider.name}</div>
                  {provider.is_primary && <span className="text-xs text-blue-600">Primary</span>}
                </div>
                <span className={`px-2 py-0.5 rounded text-xs ${provider.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                  {provider.status}
                </span>
              </div>
              <div className="text-2xl font-bold text-green-600 mb-1">{formatCurrency(provider.volume_today)}</div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-gray-500">Success: </span>
                  <span className="font-medium">{provider.success_rate}%</span>
                </div>
                <div>
                  <span className="text-gray-500">Fee: </span>
                  <span className="font-medium">{provider.transaction_fee_pct}%</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Recent Transactions</h2></div>
            <div className="divide-y">
              {transactions.slice(0, 10).map(trans => (
                <div key={trans.id} className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-mono text-sm">{trans.id}</div>
                      <div className="text-sm text-gray-500">{trans.customer_email}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-green-600">{formatCurrency(trans.amount, trans.currency)}</div>
                      <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(trans.status)}`}>{trans.status}</span>
                    </div>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <div>
                      <span className="capitalize">{trans.provider}</span> • {trans.payment_method}
                    </div>
                    <div>Fee: {formatCurrency(trans.fee, trans.currency)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Open Disputes ({disputes.filter(d => ['open', 'under_review'].includes(d.status)).length})</h3>
              <div className="space-y-3">
                {disputes.filter(d => ['open', 'under_review'].includes(d.status)).slice(0, 5).map(dispute => (
                  <div key={dispute.id} className="p-3 bg-gray-50 rounded border-l-4 border-yellow-500">
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-medium text-sm capitalize">{dispute.reason.replace(/_/g, ' ')}</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(dispute.status)}`}>
                        {dispute.status.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <div className="font-bold text-red-600 mb-1">{formatCurrency(dispute.amount, dispute.currency)}</div>
                    <div className="text-xs text-gray-500">
                      Evidence due: {new Date(dispute.evidence_due_date).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Payment Methods</h3>
              <div className="space-y-2">
                {['visa', 'mastercard', 'amex', 'paypal_balance', 'apple_pay'].map(method => (
                  <div key={method} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                    <span className="capitalize text-sm">{method.replace(/_/g, ' ')}</span>
                    <span className="text-xs text-green-600">Active</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
